import { NgOptimizedImage } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { form, FormField } from '@angular/forms/signals';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { finalize } from 'rxjs';
import { AuthService } from '../../../../../core/services/auth/auth.service';
import { LucideModule } from '../../../../../shared/modules/lucide.module';
import { mensagemDeErroLogin } from '../../../../../shared/utils/mensagem-erro-login';
import { NormalizeService } from '../../../../../shared/utils/normalize.service';

/** Card de acesso do e-SIC, exibido à direita nas etapas de orientações e identificação. */
@Component({
  selector: 'app-cartao-acesso-sic',
  templateUrl: './cartao-acesso-sic.component.html',
  styleUrls: ['./cartao-acesso-sic.component.css'],
  imports: [FormField, LucideModule, NgxMaskDirective, NgOptimizedImage],
  // Mantém o valor mascarado no modelo; o envio normaliza os dígitos antes de chamar a API.
  providers: [provideNgxMask({ dropSpecialCharacters: false })],
})
export class CartaoAcessoSicComponent {
  private readonly authService = inject(AuthService);
  private readonly normalizeService = inject(NormalizeService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Indica que o login foi feito dentro deste fluxo. Fica no store porque o card é recriado a cada
   * troca de etapa; com sessão ativa vinda de outra tela continua `false`, de modo que as opções
   * iniciais (login, gov.br e anônimo) seguem disponíveis aqui.
   */
  readonly sessaoIniciada = input(false);

  /** Verdadeiro quando a pessoa optou por seguir sem se identificar. */
  readonly anonimo = input(false);

  /** Cada incremento vindo do pai rola até o card e foca o campo de CPF. */
  readonly focarLogin = input(0);

  /** Liberado apenas depois que as orientações legais forem marcadas como lidas. */
  readonly bloqueado = input(false);

  readonly mensagemBloqueio = input('');

  readonly continuar = output<void>();

  readonly autenticado = output<void>();

  readonly acessarGov = output<void>();

  readonly seguirAnonimo = output<void>();

  readonly saiu = output<void>();

  /** Disparado quando o card é acionado antes das orientações serem aceitas. */
  readonly bloqueioAcionado = output<void>();

  private readonly usuario = toSignal(this.authService.currentUser, {
    initialValue: this.authService.currentUserValue,
  });

  readonly logado = computed(() => this.sessaoIniciada() && !!this.usuario());

  readonly nomeUsuario = computed(() => this.usuario()?.nome?.trim() || 'cidadão');

  /** Máscara nativa do ngx-mask: alterna entre CPF e CNPJ conforme os dígitos digitados. */
  readonly mascaraDocumento = 'CPF_CNPJ';

  readonly dados = signal({ documento: '', senha: '' });
  readonly loginForm = form(this.dados);

  readonly senhaVisivel = signal(false);
  readonly entrando = signal(false);
  readonly erroLogin = signal('');
  readonly tentouEntrar = signal(false);

  readonly documentoValido = computed(() => {
    const digitos = this.dados().documento.replace(/\D/g, '');
    return digitos.length === 11 || digitos.length === 14;
  });

  readonly senhaValida = computed(() => this.dados().senha.trim().length > 0);

  readonly loginValido = computed(() => this.documentoValido() && this.senhaValida());

  private readonly cartao = viewChild<ElementRef<HTMLElement>>('cartao');
  private readonly campoDocumento = viewChild<ElementRef<HTMLInputElement>>('campoDocumento');

  constructor() {
    effect(() => {
      if (this.focarLogin() === 0) return;

      const campo = this.campoDocumento()?.nativeElement;
      const cartao = this.cartao()?.nativeElement;

      (campo ?? cartao)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      campo?.focus({ preventScroll: true });
    });
  }

  alternarSenha(): void {
    this.senhaVisivel.update((visivel) => !visivel);
  }

  entrar(evento: Event): void {
    evento.preventDefault();

    if (this.recusadoPeloBloqueio()) return;

    this.tentouEntrar.set(true);
    this.erroLogin.set('');

    if (!this.loginValido() || this.entrando()) {
      return;
    }

    const { documento, senha } = this.dados();
    const usuario = this.normalizeService.cpfCnpj(documento);

    this.entrando.set(true);

    this.authService
      .login(usuario, senha)
      .pipe(
        finalize(() => this.entrando.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.dados.update((atual) => ({ ...atual, senha: '' }));
          this.tentouEntrar.set(false);
          this.autenticado.emit();
        },
        error: (erro) => this.erroLogin.set(mensagemDeErroLogin(erro)),
      });
  }

  entrarComGov(evento: Event): void {
    evento.preventDefault();

    if (this.recusadoPeloBloqueio()) return;

    this.acessarGov.emit();
  }

  solicitarAnonimamente(): void {
    if (this.recusadoPeloBloqueio()) return;

    this.seguirAnonimo.emit();
  }

  sair(): void {
    this.authService.logout();
    this.dados.set({ documento: '', senha: '' });
    this.tentouEntrar.set(false);
    this.erroLogin.set('');
    this.senhaVisivel.set(false);
    this.saiu.emit();
  }

  private recusadoPeloBloqueio(): boolean {
    if (!this.bloqueado()) return false;

    this.bloqueioAcionado.emit();
    return true;
  }
}
